// App.js
import React, { useEffect, lazy, Suspense } from "react";
import "./i18n";
import { AppDialogProvider } from "./components/app/AppDialogProvider";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
  Navigate,
} from "react-router-dom";
import Root from "./pages/Root";
import Home from "./pages/Home";
import LoginPage from "./pages/LoginPage";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ProtectedRoute from "./pages/ProtectedRoute";
import { Snackbar, Alert } from "@mui/material";
import ThemeWrapper from "./ThemeWrapper";
import AppInitializer from "./components/app/AppInitializer";
import LoadingFallback from "./components/app/LoadingFallback";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import {
  login,
  logout,
  selectIsAuthenticated,
  selectUserDecoded,
} from "./redux_files/authSlice";
import { logAuditEvent } from "./utils/auditLog";

// Lazy load heavy components for better performance
const WholesaleDashboard = lazy(() => import("./pages/Dashboards/WholesaleDashboard"));
const POSDashboard = lazy(() => import("./pages/Dashboards/POSDashboard"));
const ShiftClosingsOpenings = lazy(() =>
  import("./pages/Dashboards/ShiftClosingsOpenings")
);
const ItemCostByPeriod = lazy(() => import("./pages/Dashboards/ItemCostByPeriod"));
const PrepareItemCostByPeriod = lazy(() => import("./pages/Dashboards/PrepareItemCostByPeriod"));
const ItemCostByPeriodList = lazy(() => import("./pages/Dashboards/ItemCostByPeriodList"));
const BranchCurrentInventory = lazy(() => import("./pages/Dashboards/BranchCurrentInventory"));
const HourlyInventoryBalance = lazy(() => import("./pages/Dashboards/HourlyInventoryBalance"));
const HourlyInventoryBalanceHistory = lazy(() => import("./pages/Dashboards/HourlyInventoryBalanceHistory"));
const PastryOutageHoursSummary = lazy(() => import("./pages/Dashboards/PastryOutageHoursSummary"));
const HourlyInventorySnapshotSettings = lazy(() => import("./pages/Managment/HourlyInventorySnapshotSettings"));
const SitDispatchFilterSettings = lazy(() => import("./pages/Managment/SitDispatchFilterSettings"));
const CitiesPage = lazy(() => import("./pages/Managment/CitiesPage"));
const CountriesPage = lazy(() => import("./pages/Managment/CountriesPage"));
const SalesAreasPage = lazy(() => import("./pages/Managment/SalesAreasPage"));
const EmployeesPage = lazy(() => import("./pages/Managment/EmployeesPage"));
const WarehousesPage = lazy(() => import("./pages/Managment/WarehousesPage"));
const ItemCostCategoriesPage = lazy(() =>
  import("./pages/Managment/ItemCostCategoriesPage")
);
const HourlyItemSnapshotsPage = lazy(() =>
  import("./pages/Managment/HourlyItemSnapshotsPage")
);
const POSLiveData = lazy(() => import("./pages/Dashboards/POSLiveData"));
const Vendor = lazy(() => import("./pages/TrialBalance/Vendor"));
const POSDashboardWithCombo = lazy(() => import("./pages/Dashboards/POSDashboardWithCombo"));
const SalesDifferenceDashboard = lazy(() =>
  import("./pages/Dashboards/SalesDifferenceDashboard")
);
const PromotionItems = lazy(() => import("./pages/Dashboards/PromotionItems"));
const PromotionItemsForm = lazy(() => import("./pages/Dashboards/PromotionItemsForm"));
const SupDashboard = lazy(() => import("./pages/Dashboards/SupDashboard"));
const UserManagementPage = lazy(() => import("./pages/Managment/UserManagementPage"));
const AuditLogPage = lazy(() => import("./pages/Managment/AuditLogPage"));
const LegacySalesArchivePage = lazy(() =>
  import("./pages/Managment/LegacySalesArchivePage")
);
const LegacySalesSettingsPage = lazy(() =>
  import("./pages/Managment/LegacySalesSettingsPage")
);
const RmsProductCategoriesPage = lazy(() =>
  import("./pages/Managment/RmsProductCategoriesPage")
);
const RmsProductsPage = lazy(() => import("./pages/Managment/RmsProductsPage"));
const UserProfilePage = lazy(() => import("./pages/Profile/UserProfilePage"));
const InventoryList = lazy(() => import("./pages/Inventory/InventoryPage/InventoryList"));
const InventoryPg = lazy(() => import("./pages/Inventory/InventoryPage/InventoryPg"));
const UsedCardsByLoyaltyProgram = lazy(() => import("./pages/cards_coupons/UsedCardsByLoyaltyProgram"));
const OrdersSourcePerHour = lazy(() => import("./pages/Dashboards/OrdersSourcePerHour"));
const OrdersBranchSourcePerHour = lazy(() => import("./pages/Dashboards/OrdersBranchSourcePerHour"));
const OrdersBranchSourcePerPeriod = lazy(() => import("./pages/Dashboards/OrdersBranchSourcePerPeriod"));
const DispatchByCategory = lazy(() => import("./pages/Sit/DispatchByCategory"));
const DispatchByItem = lazy(() => import("./pages/Sit/DispatchByItem"));
const DispatchByDetails = lazy(() => import("./pages/Sit/DispatchByDetails"));
const WHDashboard = lazy(() => import("./pages/Dashboards/WHDashboard"));
const WholesaleCollectionsDashboard = lazy(() => import("./pages/Dashboards/WholesaleCollectionsDashboard"));
const BranchesList = lazy(() => import("./pages/Branches/BranchesList"));
const BranchDetails = lazy(() => import("./pages/Branches/BranchDetails"));
const BranchProfile = lazy(() => import("./pages/Branches/BranchProfile"));
const AddBranch = lazy(() => import("./pages/Branches/AddBranch"));
const POSSalesArchivePage = lazy(() => import("./pages/POSSalesArchive/POSSalesArchivePage"));
const WholesaleSalesArchivePage = lazy(() =>
  import("./pages/WholesaleSalesArchive/WholesaleSalesArchivePage")
);

function App() {
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const decoded = useSelector(selectUserDecoded);

  const handleLogin = (data) => {
    dispatch(login(data));
  };

  const handleLogout = () => {
    logAuditEvent("logout");
    dispatch(logout());
  };

  useEffect(() => {
    const savedLang = localStorage.getItem("language");
    if (savedLang && savedLang !== i18n.language) {
      void i18n.changeLanguage(savedLang);
    }
  }, [i18n]);

  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />
        <Route
          path="/"
          element={<Root handleLogout={handleLogout} decoded={decoded} />} // تم تمرير decoded هنا
        >
          {!isAuthenticated || !decoded ? (
            <Route index element={<Navigate to="/login" replace />} />
          ) : !decoded["can_access_system"] ? (
            <Route
              index
              element={
                <>
                  <Navigate to="/login" replace />
                  <Snackbar open autoHideDuration={4000}>
                    <Alert severity="error">
                      {t("Access Denied to System")}
                    </Alert>
                  </Snackbar>
                </>
              }
            />
          ) : (
            <>
              <Route index element={<Navigate to="/home" />} />
              <Route path="home" element={<Home />} />
              <Route
                path="profile"
                element={
                  <Suspense fallback={<LoadingFallback />}>
                    <UserProfilePage />
                  </Suspense>
                }
              />
              <Route
                path="POSDashboard"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_pos_dashboard"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback message="جاري تحميل لوحة التحكم..." />}>
                      <POSDashboard />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="POSDashboard"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_pos_dashboard"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback message="جاري تحميل لوحة التحكم..." />}>
                      <POSDashboard />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="POSDashboardWithCombo"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_pos_dashboard"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <POSDashboardWithCombo />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="SalesDifference"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_sales_difference"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <SalesDifferenceDashboard />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="shift-closings-openings"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_shift_gaps_report"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <ShiftClosingsOpenings />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="promotion-items"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_promotion_items"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <PromotionItems />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="promotion-items/new"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_promotion_items"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <PromotionItemsForm />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="promotion-items/:id/edit"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_promotion_items"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <PromotionItemsForm />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="SupDashboard"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_Supervisors_dashboard"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <SupDashboard />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="WholesaleDashboard"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_wholesale_dashboard"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <WholesaleDashboard />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="vendor"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_supplier_review_balance"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <Vendor />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="prepare-item-cost-by-period"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_items_cost_by_period"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <PrepareItemCostByPeriod />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="item-cost-by-period-list"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_items_cost_by_period"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <ItemCostByPeriodList />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="item-cost-by-period/:reportId"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_items_cost_by_period"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <ItemCostByPeriod />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="branch-current-inventory"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_branch_current_inv"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <BranchCurrentInventory />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="hourly-inventory-balance"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_hourly_inventory_balance"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <HourlyInventoryBalance />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="hourly-inventory-balance-history"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_hourly_inventory_balance_history"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <HourlyInventoryBalanceHistory />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="pastry-outage-hours-summary"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_pastry_outage_hours_summary"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <PastryOutageHoursSummary />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="hourly-inventory-snapshot-settings"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_manage_hourly_inventory_snapshot_settings"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <HourlyInventorySnapshotSettings />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="sit-dispatch-filter-settings"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_manage_sit_dispatch_filter_settings"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <SitDispatchFilterSettings />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="cities"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_manage_cities"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <CitiesPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="countries"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_manage_countries"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <CountriesPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="sales-areas"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_manage_sales_areas"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <SalesAreasPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="employees"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_manage_employees"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <EmployeesPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="warehouses"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_manage_warehouses"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <WarehousesPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="item-cost-categories"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_manage_item_cost_categories"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <ItemCostCategoriesPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="hourly-item-snapshots"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_hourly_item_snapshots"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <HourlyItemSnapshotsPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="POSLiveData"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_pos_dashboard"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <POSLiveData />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="UserManagement"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="repAPI.can_manage_users"
                    fallback="/UserManagement"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <UserManagementPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="audit-log"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_audit_log"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <AuditLogPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="legacy-sales-archive"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_legacy_sales_archive"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <LegacySalesArchivePage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="legacy-sales-settings"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_legacy_sales_archive"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <LegacySalesSettingsPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="rms-product-categories"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_products"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <RmsProductCategoriesPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="rms-products"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_products"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <RmsProductsPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="inventory"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_inventory_count"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <InventoryList />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="inventory-new/list"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_inventory_count"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <InventoryList />
                    </Suspense>
                  </ProtectedRoute>
                }
              />

              <Route
                path="inventory-new/create"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_inventory_count"
                    fallback="/inventory-new/list"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <InventoryPg decoded={decoded} />
                    </Suspense>
                  </ProtectedRoute>
                }
              />

              <Route
                path="inventory-new/:id"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_inventory_count"
                    fallback="/inventory-new/list"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <InventoryPg decoded={decoded} />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="UsedCardsByLoyaltyProgram"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_used_cards_report"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <UsedCardsByLoyaltyProgram />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="OrdersSourcePerHour"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_orders_source_per_hour_report"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <OrdersSourcePerHour />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="OrdersBranchSourcePerHour"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_orders_source_per_branch_report"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <OrdersBranchSourcePerHour />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="OrdersBranchSourcePerPeriod"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_orders_source_per_branch_period_report"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <OrdersBranchSourcePerPeriod />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="dispatch-by-category"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_sit_dispatch_by_category"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <DispatchByCategory />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="dispatch-by-item"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_sit_dispatch_by_item"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <DispatchByItem />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="dispatch-by-details"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_sit_dispatch_by_details"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <DispatchByDetails />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="WHDashboard"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_sit_dispatch_by_item"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <WHDashboard />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="WholesaleCollectionsDashboard"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_wholesale_dashboard"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <WholesaleCollectionsDashboard />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="branches"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_branches"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <BranchesList />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="branches/add"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_add_new_branch"
                    fallback="/branches"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <AddBranch />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="branches/:branchId/edit"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_branches"
                    fallback="/branches"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <BranchDetails />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="branches/:branchId"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_branches"
                    fallback="/branches"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <BranchProfile />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="pos-sales-archive"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_pos_sales_archive"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <POSSalesArchivePage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="wholesale-sales-archive"
                element={
                  <ProtectedRoute
                    decoded={decoded}
                    permission="can_access_wholesale_sales_archive"
                    fallback="/home"
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <WholesaleSalesArchivePage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
            </>
          )}
        </Route>

        <Route
          path="*"
          element={<Navigate to={isAuthenticated ? "/home" : "/login"} />}
        />
      </>
    )
  );

  return (
    <ThemeWrapper>
      <AppInitializer>
        <AppDialogProvider>
          <RouterProvider router={router} />
        </AppDialogProvider>
      </AppInitializer>
    </ThemeWrapper>
  );
}

export default App;
